import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth } from 'date-fns';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { DraggableBlocksContainer } from './DraggableBlocksContainer';
import { QuickActionsBar } from './QuickActionsBar';
import { AwaitingMyActionCard } from './AwaitingMyActionCard';
import { useAuth } from '@/features/auth';
import { kpiApi, kpiQueryKeys } from '@/features/kpi/api';
import { useLawyerKpi } from '../hooks/useLawyerKpi';
import { usePersonalBlocks } from './personalBlocks';


function StatCard({ label, value, sub, to }: { label: string; value: React.ReactNode; sub: React.ReactNode; to?: string }) {
  const body = (
    <>
      <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-semibold tabular-nums leading-[1.3] text-slate-900">{value}</div>
      <div className="text-[0.8125rem] mt-1 flex items-center gap-1.5">{sub}</div>
    </>
  );
  const cls = 'block bg-gradient-to-t from-white to-[rgba(0,105,209,0.04)] border border-slate-200 rounded-xl shadow-xs p-6 hover:shadow-md transition-shadow';
  if (to) {
    return (
      <Link to={to} className={`${cls} hover:border-[#0069D1]/40`}>
        {body}
      </Link>
    );
  }
  return <div className={cls}>{body}</div>;
}

export function LawyerDashboard() {
  const { t } = useTranslation('common');
  const { hasAccess, user } = useAuth();

  const { data: kpi, isLoading: kpiLoading } = useLawyerKpi();

  // KPI This Month — the real KPI engine over the current calendar month
  // (1st → today). The card links to /app/kpi with the same range pre-set.
  const monthFrom = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const monthTo = format(new Date(), 'yyyy-MM-dd');
  const { data: monthKpi, isLoading: monthKpiLoading } = useQuery({
    queryKey: kpiQueryKeys.summary({ userId: user?.id ?? 0, from: monthFrom, to: monthTo }),
    queryFn: () => kpiApi.getSummary({ userId: user!.id, from: monthFrom, to: monthTo }),
    enabled: !!user?.id && hasAccess('kpi', 'get'),
  });

  // TS Lot 2 §3.1.1 — my matters / meetings / deadlines, shared with the MD
  // dashboard (client finding D-01).
  const personalBlocks = usePersonalBlocks();

  const blocks = [
    // Case 360° (Stage 12) — draggable like every other dashboard block.
    { id: 'quick-actions', label: t('dashboard.quickActions'), content: <QuickActionsBar /> },
    { id: 'awaiting-my-action', label: t('dashboard.case360.awaitingTitle'), content: <AwaitingMyActionCard /> },
    {
      id: 'stats',
      label: t('dashboard.statistics'),
      content: (
        <div className="grid grid-cols-4 gap-4 md:grid-cols-2">
          <StatCard
            label={t('dashboard.activeMatters')}
            value={kpiLoading ? '—' : (kpi?.active_matters ?? '—')}
            sub={
              <span className="font-semibold text-slate-500">
                {kpiLoading ? '' : t('dashboard.hoursThisMonth', { hours: (kpi?.month_hours ?? 0).toFixed(1) })}
              </span>
            }
          />
          <StatCard
            label={t('dashboard.approvedHoursMonth')}
            value={kpiLoading ? '—' : <>{(kpi?.month_hours ?? 0).toFixed(1)} <span className="text-[0.875rem] font-medium text-slate-400">hrs</span></>}
            sub={
              <span className={`font-semibold ${(kpi?.utilization ?? 0) >= 75 ? 'text-green-600' : (kpi?.utilization ?? 0) >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                {kpiLoading ? '' : `${(kpi?.utilization ?? 0).toFixed(1)}% ${t('dashboard.utilization')}`}
              </span>
            }
          />
          <StatCard
            label={t('dashboard.myRating')}
            value={
              kpiLoading
                ? '—'
                : kpi?.avg_rating == null
                  ? '—'
                  : <>{kpi.avg_rating.toFixed(2)} <span className="text-[0.875rem] font-medium text-slate-400">/ {kpi?.rating_scale ?? 5}</span></>
            }
            sub={
              kpiLoading ? null : kpi?.avg_rating == null ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium border bg-slate-50 text-slate-500 border-slate-200">
                  {t('dashboard.noReviewsYet')}
                </span>
              ) : (
                // Bands on the K3 1–5 scale (TS Lot 1 §10.2): 4+ good, 3+ fair.
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium border ${
                  kpi.avg_rating >= 4
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : kpi.avg_rating >= 3
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-red-50 text-red-700 border-red-100'
                }`}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: kpi.avg_rating >= 4 ? '#16a34a' : kpi.avg_rating >= 3 ? '#d97706' : '#ef4444' }} />
                  {t('dashboard.reviewsLast12Months', { count: kpi.review_count })}
                </span>
              )
            }
          />
          <StatCard
            label={t('dashboard.kpiThisMonth')}
            to={`/app/kpi?userId=${user?.id}&from=${monthFrom}&to=${monthTo}`}
            value={
              monthKpiLoading
                ? '—'
                : monthKpi?.scores?.overall == null
                  ? '—'
                  : <span className={
                      monthKpi.scores.overall >= 75 ? 'text-green-600' : monthKpi.scores.overall >= 50 ? 'text-amber-600' : 'text-red-600'
                    }>{monthKpi.scores.overall}%</span>
            }
            sub={
              <span className="font-semibold text-slate-500">
                {monthKpiLoading
                  ? ''
                  : t('dashboard.kpiCardSub', {
                      tasks: monthKpi?.productivity?.tasksDone ?? 0,
                      hours: (monthKpi?.productivity?.hoursLogged ?? 0).toFixed(1),
                    })}
              </span>
            }
          />
        </div>
      ),
    },
    // ⚠ The three §3.1.1 personal blocks below (matters / calendar / deadlines)
    // now live in `personalBlocks.tsx` so the MD dashboard mounts the same
    // components instead of a second copy (client finding D-01). Their ids are
    // unchanged, so a saved block order still resolves.
    ...personalBlocks,
  ];

  return (
    <DraggableBlocksContainer storageKey="andersen.lawyer.blockOrder" blocks={blocks} />
  );
}
